import React, {useMemo, useRef, useReducer, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {orderBy} from 'lodash';

import {FilterConfiguration, FilterTypes} from '@ey/canvascoreservices/Filters';

import {labels, currentResource} from '../../../../../util/utils';
import {ContactsDBFilterTypeQuery, notApplicableId} from '../../../../../util/uiconstants';

import {
	ContactsDBFilterEntityConfiguration,
	FilterTypeConfiguration,
	ContactsDBFilterEntityTypes
} from '../../../../Common/Filter/config';

import Filters from './ContactsDBFilters';
import ContactsDBFilterControls from './ContactsDBFilterControls';

import {getFilterEntities} from '../../../../../actions/Filter/FilterActions';
import getFilterCount from './getFilterCount';

export const ContactsFilter = (props) => {
	const dispatchAction = useDispatch();
	const project = useSelector((state) => state.project);
	const {isGroupRiskAssessmentProject} = project;
	const contactsDB = useSelector((state) => state.contacts);

	const [numberOfChips, setNumberOfChips] = useState(4);

	const getNumberOfChips = (w) => {
		if (w >= 1800) return 7;
		if (w >= 1600) return 6;
		if (w >= 1400) return 5;
		return 4;
	};

	useEffect(() => {
		const handleResize = () => {
			const {innerWidth: width} = window;
			setNumberOfChips(getNumberOfChips(width));
		};

		const {innerWidth: width} = window;
		setNumberOfChips(getNumberOfChips(width));

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const {actions, location} = props;

	const renderFilters = {};

	const Toolbar = {
		filters: {
			renderItem: (props) => (
				<Filters
					project={project}
					filterConfiguration={props.filterConfiguration}
					state={props.state}
					setState={props.setState}
					history={props.history}
					dispatch={props.dispatch}
					_syncQueryString={props._syncQueryString}
				/>
			),
			onRender: (args, renderProps) => {
				const projectId = location.search.split('projectId=')[1].substring(0, 36);

				const filterKey = args.filterKey || `${projectId}#ContactsDB`;

				const getFilterEntity = (filterItemName, entityType, entityFilter, filter) => {
					let _entityFilter = {...entityFilter};

					// Get entity filter function, if any
					let __entityFilter = ContactsDBFilterEntityConfiguration[entityType]?.entityFilter;
					if (__entityFilter) {
						_entityFilter = __entityFilter(_entityFilter);
					}

					return dispatchAction(getFilterEntities(filterKey, filterItemName, entityType, _entityFilter));
				};

				const getFilterEntityByRedux = (filterItemName, entityType, entityFilter) => {
					let contacts = contactsDB.entities.flatMap((entities) => entities.contacts);

					if (filterItemName === ContactsDBFilterTypeQuery.byServiceLine) {
						let serviceLineContacts = [...contacts];

						let servicesLines = [];
						Array.from(new Set(serviceLineContacts.map((serviceLine) => serviceLine.serviceLineId))).map(
							(serviceLineId) => {
								if (serviceLineId !== undefined) {
									servicesLines.push({
										value: serviceLineId,
										label: serviceLineContacts.find((e) => e.serviceLineId === serviceLineId).serviceLineName
									});
								}
							}
						);

						servicesLines = orderBy(servicesLines, (serviceLineContact) => serviceLineContact.label);

						dispatchAction(
							getFilterEntities(filterKey, filterItemName, entityType, entityFilter, null, false, servicesLines)
						);

						return servicesLines;
					}

					if (filterItemName === ContactsDBFilterTypeQuery.byRole) {
						let roleContacts = [...contacts];

						let roles = [];
						Array.from(new Set(roleContacts.map((role) => role.roleId))).map((roleId) => {
							if (roleId !== undefined) {
								roles.push({
									value: roleId,
									label: roleContacts.find((e) => e.roleId === roleId).roleName
								});
							}
						});

						roles = orderBy(roles, (role) => role.label);

						dispatchAction(getFilterEntities(filterKey, filterItemName, entityType, entityFilter, null, false, roles));

						return roles;
					}
				};

				const entityFilter = {
					projectId: projectId
				};

				// Calculate filterConfiguration just once and pass it down to renderItem function later
				renderProps.filterConfiguration = new FilterConfiguration(filterKey, {
					contactsbycountry: {
						label: labels.countryFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: ContactsDBFilterEntityTypes.country,
						entityFilter: ContactsDBFilterEntityConfiguration.country.entityFilter(entityFilter),
						data: () =>
							getFilterEntity(ContactsDBFilterTypeQuery.byCountry, ContactsDBFilterEntityTypes.country, entityFilter),
						dataMapping: ContactsDBFilterEntityConfiguration.country.entityMapping,
						getDisplayValue: ContactsDBFilterEntityConfiguration.country.getDisplayValue
					},
					contactsbygrouprole: {
						label: labels.groupRoleFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						data: currentResource.groupRole,
						getDisplayValue: ContactsDBFilterEntityConfiguration.groupRole.getDisplayValue
					},
					contactsbyengagement: {
						label: labels.engagementsContacts,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: ContactsDBFilterEntityTypes.engagement,
						entityFilter: ContactsDBFilterEntityConfiguration.engagement.entityFilter(entityFilter),
						data: () =>
							getFilterEntity(
								ContactsDBFilterTypeQuery.byEngagement,
								ContactsDBFilterEntityTypes.engagement,
								entityFilter
							),
						dataMapping: ContactsDBFilterEntityConfiguration.engagement.entityMapping,
						getDisplayValue: ContactsDBFilterEntityConfiguration.engagement.getDisplayValue
					},
					type: {
						label: labels.typeFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						data: currentResource.userType,
						getDisplayValue: ContactsDBFilterEntityConfiguration.userType.getDisplayValue
					},
					contactsbyserviceline: {
						label: labels.serviceLineFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: ContactsDBFilterEntityTypes.serviceline,
						entityFilter: ContactsDBFilterEntityConfiguration.serviceLine.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							ContactsDBFilterTypeQuery.byServiceLine,
							ContactsDBFilterEntityTypes.serviceline,
							entityFilter
						),
						getDisplayValue: ContactsDBFilterEntityConfiguration.serviceLine.getDisplayValue
					},
					contactsbyrole: {
						label: labels.roleFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: ContactsDBFilterEntityTypes.role,
						entityFilter: ContactsDBFilterEntityConfiguration.role.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							ContactsDBFilterTypeQuery.byRole,
							ContactsDBFilterEntityTypes.role,
							entityFilter
						),
						getDisplayValue: ContactsDBFilterEntityConfiguration.role.getDisplayValue
					},
					dashboardaccess: {
						label: labels.dashboardAccessFilter,
						type: FilterTypes.boolean,
						getDisplayValue: FilterTypeConfiguration[FilterTypes.boolean].getDisplayValue
					},
					...(isGroupRiskAssessmentProject && {
						groupAccess: {
							label: labels.columnGroupRiskAssessment,
							type: FilterTypes.boolean,
							getDisplayValue: FilterTypeConfiguration[FilterTypes.boolean].getDisplayValue
						}
					}),
					contactlist: {
						label: labels.contactListFilter,
						type: FilterTypes.boolean,
						getDisplayValue: FilterTypeConfiguration[FilterTypes.boolean].getDisplayValue
					},
					contactsbytag: {
						label: labels.tagFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: ContactsDBFilterEntityTypes.tag,
						entityFilter: ContactsDBFilterEntityConfiguration.tag.entityFilter(entityFilter),
						data: () => getFilterEntity(ContactsDBFilterTypeQuery.byTag, ContactsDBFilterEntityTypes.tag, entityFilter),
						dataMapping: ContactsDBFilterEntityConfiguration.tag.entityMapping,
						getDisplayValue: ContactsDBFilterEntityConfiguration.tag.getDisplayValue
					}
				});
			}
		},
		filterChips: {
			renderItem: (props) => (
				<ContactsDBFilterControls
					key={props.key}
					project={project}
					filterConfiguration={props.filterConfiguration}
					state={props.state}
					setState={props.setState}
					history={props.history}
					dispatch={props.dispatch}
					numberOfChips={numberOfChips}
					_syncQueryString={props._syncQueryString}
				/>
			)
		}
	};

	const toolbar = [
		{
			className: 'filters',
			components: [Toolbar.filters]
		},
		{
			className: 'filter-chips',
			components: [Toolbar.filterChips]
		}
	];

	const [renderProps, filterKey] = useMemo(() => {
		let renderProps, filterKey;

		if (toolbar?.length > 0) {
			renderProps = {
				project
			};

			for (let i = 0; i < toolbar.length; i++) {
				const row = toolbar[i];

				for (let k = 0; k < row.components.length; k++) {
					const component = row.components[k];

					if (component.onRender) {
						component.onRender(
							// renderFilters object is mutable and might modified onRender
							{
								actions,
								location,
								project,
								renderFilters,
								filterKey: component.filterKey,
								history: props.history
							},

							// renderProps object is mutable and might modified onRender
							renderProps
						);
					}
				}
			}

			filterKey = renderProps.filterConfiguration?.key;
		}

		return [renderProps, filterKey];
	}, [project]);

	const reducer = (state, action) => {
		switch (action.type) {
			case 'state/set': {
				return {
					...state,
					...action.value
				};
			}

			default:
				return state;
		}
	};

	let initialState = {};
	renderProps._syncQueryString = useRef(renderProps.filterConfiguration ? true : undefined);
	if (renderProps.filterConfiguration) {
		initialState.filters = renderProps.filterConfiguration.state;
	}

	const [state, dispatch] = useReducer(reducer, initialState);

	return (
		<>
			{toolbar.map((row, index) => (
				<div
					key={`${index}`}
					className={row.className}
					style={row.style}
					numberOfChips={numberOfChips}
					numberOfSelectedChips={getFilterCount(state.filters)}
				>
					{row.components &&
						row.components.map((component, index) =>
							component.renderItem({
								key: `${index}`,
								...renderProps,
								history: props.history,
								state,
								dispatch
							})
						)}
				</div>
			))}
		</>
	);
};

export default withRouter(ContactsFilter);
